import ManagerWorkspace from './ManagerWorkspace';
import usePagination from '../../hooks/usePagination';
import { useEffect, useState } from 'react';
import { FaArrowDown, FaArrowUp, FaEdit, FaTrash } from 'react-icons/fa';
import supabase from '../../supabase';
import './TimelineManager.css';

const EMPTY_ENTRY = {
  id: null,
  date_label: '',
  org_label: '',
  title: '',
  description: '',
  published: true,
};

const TimelineManager = () => {
  const [entries, setEntries] = useState([]);
  const [view, setView] = useState('browse');
  const [query, setQuery] = useState('');
  const filteredItems = entries.filter(item => JSON.stringify(item).toLowerCase().includes(query.trim().toLowerCase()));
  const pagination = usePagination(filteredItems, 6, query);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentEntry, setCurrentEntry] = useState(EMPTY_ENTRY);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      setError(null);

      const { error: tableError } = await supabase
        .from('timeline')
        .select('count')
        .limit(1);

      if (tableError) {
        if (tableError.code === '42P01') {
          setError(
            'The timeline table does not exist yet. Please run server/sql/2026-07-11_create_timeline_table.sql in the Supabase SQL Editor.'
          );
          setEntries([]);
          return;
        }
        throw tableError;
      }

      const { data, error } = await supabase
        .from('timeline')
        .select('*')
        .order('order_index', { ascending: true });

      if (error) throw error;
      setEntries(data || []);
    } catch (err) {
      console.error('Error fetching timeline:', err);
      setError('Failed to fetch timeline entries. Please check your Supabase configuration.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentEntry({
      ...currentEntry,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const entryData = {
        date_label: currentEntry.date_label,
        org_label: currentEntry.org_label || null,
        title: currentEntry.title,
        description: currentEntry.description,
        published: currentEntry.published,
      };

      let result;
      if (isEditing) {
        result = await supabase.from('timeline').update(entryData).eq('id', currentEntry.id);
      } else {
        result = await supabase
          .from('timeline')
          .insert([{ ...entryData, order_index: entries.length }]);
      }

      if (result.error) throw result.error;

      resetForm();
      fetchEntries();
    } catch (err) {
      console.error('Error saving timeline entry:', err);
      setError(err.message);
    }
  };

  const handleEdit = (entry) => {
    setCurrentEntry({
      id: entry.id,
      date_label: entry.date_label,
      org_label: entry.org_label || '',
      title: entry.title,
      description: entry.description,
      published: entry.published,
    });
    setIsEditing(true);
    setView('editor');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this timeline entry?')) return;
    try {
      const { error } = await supabase.from('timeline').delete().eq('id', id);
      if (error) throw error;
      fetchEntries();
    } catch (err) {
      console.error('Error deleting timeline entry:', err);
      setError(err.message);
    }
  };

  const handleMove = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= entries.length) return;

    const reordered = [...entries];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];

    try {
      const updates = reordered.map((entry, i) =>
        supabase.from('timeline').update({ order_index: i }).eq('id', entry.id)
      );
      const results = await Promise.all(updates);
      const failed = results.find((r) => r.error);
      if (failed) throw failed.error;
      setEntries(reordered.map((entry, i) => ({ ...entry, order_index: i })));
    } catch (err) {
      console.error('Error reordering timeline:', err);
      setError(err.message);
      fetchEntries();
    }
  };

  const resetForm = () => {
    setView('browse');
    setCurrentEntry(EMPTY_ENTRY);
    setIsEditing(false);
  };

  if (loading) return <div>Loading timeline...</div>;

  return (
    <div className="timeline-manager">


      {error && (
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
          {error.includes('table does not exist') && (
            <div className="setup-instructions">
              <h4>Setup Instructions:</h4>
              <ol>
                <li>Open your Supabase project dashboard</li>
                <li>Go to the SQL Editor</li>
                <li>Copy the contents of <code>server/sql/2026-07-11_create_timeline_table.sql</code></li>
                <li>Paste and run the SQL script</li>
                <li>Return to this page and refresh</li>
              </ol>
            </div>
          )}
        </div>
      )}

      <ManagerWorkspace view={view} setView={setView} isEditing={isEditing} query={query} setQuery={setQuery} pagination={pagination} label="entries" editor={<>
      <h2>{isEditing ? 'Edit Timeline Entry' : 'Add New Timeline Entry'}</h2>
      <form onSubmit={handleSubmit} className="timeline-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="date_label">Year / Date</label>
            <input
              type="text"
              id="date_label"
              name="date_label"
              value={currentEntry.date_label}
              onChange={handleInputChange}
              placeholder="e.g., 2025"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="org_label">Period / Detail (optional)</label>
            <input
              type="text"
              id="org_label"
              name="org_label"
              value={currentEntry.org_label}
              onChange={handleInputChange}
              placeholder="e.g., July – August, Ongoing"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="title">Title</label>
          <input
            type="text"
            id="title"
            name="title"
            value={currentEntry.title}
            onChange={handleInputChange}
            placeholder="e.g., Future Interns (Full-Stack Internship)"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={currentEntry.description}
            onChange={handleInputChange}
            rows={4}
            placeholder="What happened, what you built, what you earned…"
            required
          />
        </div>

        <div className="form-group checkbox-group">
          <label htmlFor="published">
            <input
              type="checkbox"
              id="published"
              name="published"
              checked={currentEntry.published}
              onChange={handleInputChange}
            />
            Published (visible on the site)
          </label>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary">
            {isEditing ? 'Update Entry' : 'Add Entry'}
          </button>
          {isEditing && (
            <button type="button" className="btn btn-secondary" onClick={resetForm}>
              Cancel
            </button>
          )}
        </div>
      </form>
      </>}>



      <p className="timeline-order-hint">
        Entries appear on the site in this order — use the arrows to reorder.
      </p>
      <div className="timeline-list">
        {entries.length === 0 ? (
          <p>No timeline entries yet. The site shows its built-in defaults until you add some.</p>
        ) : (
          pagination.items.map((entry) => (
            <div key={entry.id} className={`timeline-entry-card ${!entry.published ? 'is-draft' : ''}`}>
              <div className="timeline-entry-header">
                <div>
                  <span className="timeline-entry-date">{entry.date_label}</span>
                  {entry.org_label && <span className="timeline-entry-org"> · {entry.org_label}</span>}
                  {!entry.published && <span className="draft-badge">Draft</span>}
                </div>
                <div className="timeline-entry-controls">
                  <button
                    type="button"
                    onClick={() => handleMove(entries.indexOf(entry), -1)}
                    disabled={entries.indexOf(entry) === 0}
                    className="btn-icon-only"
                    title="Move up"
                    aria-label="Move entry up"
                  >
                    <FaArrowUp />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMove(entries.indexOf(entry), 1)}
                    disabled={entries.indexOf(entry) === entries.length - 1}
                    className="btn-icon-only"
                    title="Move down"
                    aria-label="Move entry down"
                  >
                    <FaArrowDown />
                  </button>
                </div>
              </div>

              <h3>{entry.title}</h3>
              <p>{entry.description}</p>

              <div className="timeline-entry-actions">
                <button onClick={() => handleEdit(entry)} className="btn btn-edit">
                  <FaEdit /> Edit
                </button>
                <button onClick={() => handleDelete(entry.id)} className="btn btn-delete">
                  <FaTrash /> Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      </ManagerWorkspace>
    </div>
  );
};

export default TimelineManager;
